import React from "react";
import MoleculeImage from "../MoleculeImage";
import { ChartFallbackPanel } from "./ChartFallbackPanel";

// Mirror PlotlyHost's boundary (PlotlyHost.js:71): a malformed molecule spec
// (non-object items, unexpected encodings) must degrade to the fallback panel
// rather than throw uncaught through the canvas/inline host (PLEX-4227). The
// risky work lives in the MoleculeGrid child so a throw during ITS render is
// caught here — a throw in MoleculeHost's own body would escape past us.
class MoleculeErrorBoundary extends React.Component {
  state = { error: null, lastSpec: undefined };
  static getDerivedStateFromError(error) { return { error }; }
  // Clear the error when a NEW spec arrives. Without this the boundary latches:
  // once a spec throws, onRegenerate's fallback button is permanently dead for
  // that instance, because regenerating reuses the same chart_id and so does not
  // change component identity (PLEX-4227 review). Keyed on spec identity — the
  // regenerate path produces a fresh spec object — so a re-render with the same
  // spec keeps the error rather than flickering back to a throwing child.
  static getDerivedStateFromProps(props, state) {
    if (state.lastSpec !== props.resetSpec) {
      return { error: null, lastSpec: props.resetSpec };
    }
    return null;
  }
  componentDidCatch(err, info) { console.error("[MoleculeHost] render error", err, info); }
  render() {
    if (this.state.error) {
      return <ChartFallbackPanel title={this.props.title} error={this.state.error.message} onRegenerate={this.props.onRegenerate} />;
    }
    return this.props.children;
  }
}

// MoleculeImage uses `encoding` prop (SMILES string) not `smiles` or `plexId`.
// Map spec item fields accordingly: smiles → encoding. The backend fills in the SMILES of
// an item given by plex_id; one with no SMILES in Plex still has none, and its placeholder
// shows the molecule's name rather than "???" (PLEX-6490).
// Layout and image sizing are in VisualizationCanvas.css (.molecule-host): each image fits its
// cell, whatever size the image server drew it at (PLEX-6058).
const MoleculeGrid = ({ items, layout }) => (
  <div className={`molecule-host molecule-host-${layout}`}>
    {items.map((item, i) => (
      <div key={i} className="mol-cell">
        <MoleculeImage
          encoding={item.smiles || item.encoding}
          title={item.label || item.plex_id}
          alt={item.label || item.plex_id}
        />
        {item.label && <div className="mol-label">{item.label}</div>}
      </div>
    ))}
  </div>
);

export const MoleculeHost = ({ spec, title, onRegenerate }) => {
  const items = Array.isArray(spec) ? spec : (spec && Array.isArray(spec.items) ? spec.items : null);
  if (!items) {
    return <ChartFallbackPanel title={title} error="Invalid molecule payload" onRegenerate={onRegenerate} />;
  }
  const layout = (spec && spec.layout) || "grid";

  return (
    <MoleculeErrorBoundary title={title} onRegenerate={onRegenerate} resetSpec={spec}>
      <MoleculeGrid items={items} layout={layout} />
    </MoleculeErrorBoundary>
  );
};
